import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import ApiService from '../../api/ApiService';
import LocalDataService from '../../api/LocalDataService';
import { CANVAS_CLIENT_SOURCE, getCanvasPublicUrl } from '../../config/api';
import './login.css';

function LoginMethods({ redirectPath = '/', onSuccess }) {
  const location = useLocation();
  const apiRef = useRef(new ApiService());
  const pollingRef = useRef(null);
  const expiresRef = useRef(null);
  const mountedRef = useRef(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrState, setQrState] = useState('loading');
  const [googleLoading, setGoogleLoading] = useState(false);
  const context = LocalDataService.resolveReferralParams(location.search || '');

  const stopPolling = () => {
    if (pollingRef.current) window.clearInterval(pollingRef.current);
    if (expiresRef.current) window.clearTimeout(expiresRef.current);
    pollingRef.current = null;
    expiresRef.current = null;
  };

  const checkQrStatus = async (sceneId) => {
    try {
      const refCode = context.refCode || LocalDataService.loadData('ref') || '';
      const inviterId = Number(context.userId || context.inviterId || LocalDataService.getReferrerId() || 0);
      const result = await apiRef.current.check_qrcode_status(sceneId, inviterId, refCode);
      if (!mountedRef.current) return;
      if (result?.code === 200 && result.data?.token) {
        stopPolling();
        toast.success('登录成功');
        onSuccess(result.data);
      }
    } catch (error) {
      if (error?.status && error.status !== 404) console.warn('二维码状态检查失败', error);
    }
  };

  const loadQrCode = async () => {
    stopPolling();
    if (mountedRef.current) setQrState('loading');
    try {
      const result = await apiRef.current.get_qrcode(CANVAS_CLIENT_SOURCE);
      if (!result?.url || !result?.scene_id) throw new Error('二维码响应缺少必要字段');
      const nextQrCodeUrl = await QRCode.toDataURL(result.url, { width: 224, margin: 1 });
      if (!mountedRef.current) return;
      setQrCodeUrl(nextQrCodeUrl);
      setQrState('ready');
      pollingRef.current = window.setInterval(() => checkQrStatus(result.scene_id), 1500);
      expiresRef.current = window.setTimeout(() => {
        stopPolling();
        setQrState('expired');
      }, 5 * 60 * 1000);
    } catch (error) {
      console.error('加载微信二维码失败', error);
      if (mountedRef.current) setQrState('error');
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    if (context.userId) LocalDataService.setReferrerId(context.userId);
    if (context.inviterId) LocalDataService.storeData('from_user_id', context.inviterId);
    if (context.refCode) LocalDataService.storeData('ref', context.refCode);
    loadQrCode();
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, []);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const callbackUrl = `${getCanvasPublicUrl()}/auth/google/callback`;
      localStorage.setItem('redirectAfterLogin', redirectPath);
      localStorage.setItem('canvasGoogleRedirectUri', callbackUrl);
      const result = await apiRef.current.getGoogleAuthUrl(
        context.refCode || '',
        '',
        callbackUrl,
        CANVAS_CLIENT_SOURCE
      );
      if (!result?.data?.url) throw new Error('未获取到 Google 授权地址');
      window.location.assign(result.data.url);
    } catch (error) {
      console.error('Google 登录启动失败', error);
      toast.error('暂时无法启动 Google 登录');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="canvas-login-methods">
      <header>
        <span className="canvas-login-logo">剧</span>
        <div><strong>剧查查超级工厂</strong><small>AI 创作工作台</small></div>
      </header>

      <div className="canvas-login-wechat-heading">
        <h2>微信扫码登录</h2>
        <span>推荐</span>
      </div>

      <div className="canvas-login-wechat-primary">
        <div className={`canvas-login-qr canvas-login-qr-${qrState}`}>
          {qrCodeUrl && qrState === 'ready' ? <img src={qrCodeUrl} alt="微信登录二维码" /> : null}
          {qrState === 'loading' ? <span className="canvas-login-loader">正在生成安全二维码</span> : null}
          {qrState === 'expired' ? <button type="button" onClick={loadQrCode}>二维码已过期，点击刷新</button> : null}
          {qrState === 'error' ? <button type="button" onClick={loadQrCode}>二维码加载失败，点击重试</button> : null}
        </div>
        <p className="canvas-login-qr-caption">打开微信扫一扫，授权后自动进入当前页面</p>
      </div>

      <div className="canvas-login-divider"><span />其他登录方式<span /></div>

      <button
        className="canvas-login-google"
        type="button"
        onClick={handleGoogleLogin}
        disabled={googleLoading}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09A6.8 6.8 0 0 1 5.49 12c0-.73.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.93l3.66-2.84z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
        </svg>
        {googleLoading ? '正在连接 Google…' : '使用 Google 账号登录'}
      </button>

      <footer>登录即表示同意剧查查超级工厂的服务条款与隐私政策</footer>
    </div>
  );
}

export default LoginMethods;
